// Summary: every analyst book side by side on one page, one "silo" each — its holdings
// in force today grouped by asset class (names cut to the first five words, full name on
// hover), then Total, Growth, Defensive, FX exposure, Fees & Costs, Net and the TAA tilts
// against DS70's current approved set. Same numbers as Target Weights and TAA Tilts.
import React, { useMemo } from 'react';
import { API, fmtDate, getAssetClassColor, tiltAcColour, tiltAcRank, useApiGet } from './apUtils';
import { Notice, TiltBar } from './apComponents';

const firstWords = (name, n = 5) => String(name || '').trim().split(/\s+/).slice(0, n).join(' ');
const pct = (v, dp = 1) => (v == null ? '–' : `${Number(v).toFixed(dp)}%`);
const SUMMARY_ROWS = [
  ['Growth', 'growth_exposure', 1], ['Defensive', 'defensive_exposure', 1], ['FX exposure', 'fx_exposure', 1],
  ['Fees & Costs (wtd)', 'fees_costs_wtd', 2], ['Net (wtd)', 'net_wtd', 2],
];

export default function SummaryTab({ books, reload }) {
  const codes = books.filter((b) => b.kind === 'analyst').map((b) => b.code).join(',');
  const { data, error, loading } = useApiGet(codes ? `${API}/holdings` : null, { codes }, reload, true);

  // Per book: asset-class groups (in the table's class order) with funds by weight.
  const silos = useMemo(() => {
    if (!data) return [];
    return data.portfolios.map((col) => {
      const groups = [];
      const byClass = {};
      data.holdings.forEach((h) => {
        const w = h.weights[col.code];
        if (w === undefined) return;
        if (!byClass[h.asset_class_group]) {
          byClass[h.asset_class_group] = { acg: h.asset_class_group, total: 0, funds: [] };
          groups.push(byClass[h.asset_class_group]);
        }
        byClass[h.asset_class_group].total += w;
        byClass[h.asset_class_group].funds.push({ name: h.asset_name, apir: h.apir_code, w });
      });
      groups.forEach((g) => g.funds.sort((a, b) => b.w - a.w));
      return { col, groups, total: groups.reduce((s, g) => s + g.total, 0) };
    });
  }, [data]);

  // TAA tilt rows shared by every silo (any class with a tilt in any book), one scale.
  const tiltRows = useMemo(() => {
    const present = new Set();
    let max = 2;
    Object.values(data?.taa_tilts || {}).forEach((t) => Object.entries(t).forEach(([acg, v]) => {
      if (Math.abs(v) > 0.05) present.add(acg);
      if (Math.abs(v) > max) max = Math.abs(v);
    }));
    return { items: [...present].sort((a, b) => (tiltAcRank(a) - tiltAcRank(b)) || a.localeCompare(b)), max };
  }, [data]);

  if (error) return <div className="tab-panel"><Notice kind="error">{error}</Notice></div>;
  if (!data) return <div className="tab-panel"><div className="loading-spinner">{loading ? 'Loading summary...' : 'No analyst books.'}</div></div>;

  const base = data.taa_baseline;
  return (
    <div className="tab-panel">
      {data.issues?.length > 0 && (
        <Notice kind="warn">
          Not on the AP List exactly once (shown with their saved name and class):{' '}
          {data.issues.map((i) => `${i.code} ${i.symbol}`).join(', ')}
        </Notice>
      )}
      <div className="ap-silos-scroll">
        <div className="ap-silos" style={{ gridTemplateColumns: `repeat(${silos.length}, minmax(190px, 1fr))` }}>
          {silos.map(({ col, groups, total }) => {
            const s = data.summary?.[col.code] || {};
            const feeGap = data.fee_gaps?.[col.code];
            return (
              <div key={col.code} className="ap-silo">
                <div className="ap-silo-head">
                  <div className="ap-silo-name">{col.name}</div>
                  <div className="ap-silo-sub">{col.as_at ? `Holdings from ${fmtDate(col.as_at)}` : col.basis}</div>
                </div>
                <table className="ap-silo-table">
                  <tbody>
                    {groups.map((g, gi) => (
                      <React.Fragment key={g.acg}>
                        <tr className="ap-silo-ac">
                          <td>
                            <span className="asset-class-indicator" style={{ backgroundColor: getAssetClassColor(g.acg, gi) }} />
                            {g.acg}
                          </td>
                          <td>{pct(g.total)}</td>
                        </tr>
                        {g.funds.map((f) => (
                          <tr key={`${g.acg}-${f.apir || f.name}`} className="ap-silo-fund">
                            <td title={f.name}>{firstWords(f.name)}</td>
                            <td>{pct(f.w)}</td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
                <div className="ap-silo-foot">
                  <table className="ap-silo-table ap-silo-summary">
                    <tbody>
                      <tr className="ap-silo-total"><td>Total</td><td>{pct(total)}</td></tr>
                      {SUMMARY_ROWS.map(([label, key, dp]) => (
                        <tr key={key}>
                          <td>{label}</td>
                          <td title={key.includes('wtd') && feeGap ? `No fee data for: ${feeGap.funds.join(', ')}` : undefined}>
                            {pct(s[key], dp)}{key.includes('wtd') && feeGap ? ' *' : ''}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="ap-silo-tilt-head">TAA tilts vs DS70</div>
                  <table className="ap-silo-table ap-silo-tilts">
                    <tbody>
                      {tiltRows.items.map((acg) => {
                        const colour = tiltAcColour(acg, tiltAcRank(acg));
                        const v = data.taa_tilts?.[col.code]?.[acg];
                        return (
                          <tr key={acg}>
                            <td><span className="ac-dot" style={{ background: colour }} />{acg}</td>
                            <td>{v == null ? null : <TiltBar value={v} max={tiltRows.max} color={colour} suffix="% vs DS70" />}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <p className="ap-footnote">
        Holdings in force today ({fmtDate(data.as_at_date)}); fund names cut to their first five words (hover for the full
        name). TAA tilts = holdings minus DS70&apos;s current approved set
        {base ? ` (v${base.version}, effective ${fmtDate(base.effective_from)})` : ''}, by asset class.
        {Object.keys(data.fee_gaps || {}).length > 0 && ' * Fees & Costs leave out holdings with no fee data (hover for the funds).'}
      </p>
    </div>
  );
}
